import { useEffect, useMemo, useState } from "react";
import { Navigate } from "@tanstack/react-router";
import { Download, RefreshCw, ScrollText, ShieldCheck, ShieldX, X } from "lucide-react";

import { useCurrentUser } from "~/lib/auth";
import {
    type AuditEvent,
    type AuditFilters,
    exportAuditNDJSON,
    useAuditEvent,
    useAuditList,
    useAuditVerify,
} from "~/lib/audit";
import { TableSkeleton } from "~/components/TableSkeleton";

const ACTOR_KINDS = ["", "user", "system", "api_key"] as const;

const RESOURCE_TYPES = [
    "",
    "tenant",
    "user",
    "license",
    "api_key",
    "product",
    "ioc",
    "cve",
    "rule",
    "rule_review",
    "feed",
    "service",
] as const;

const TIME_WINDOWS = [
    { label: "Anytime", value: "" },
    { label: "Last 24h", value: "24h" },
    { label: "Last 7d", value: "7d" },
    { label: "Last 30d", value: "30d" },
] as const;

function windowToFromTS(w: string): string | undefined {
    if (!w) return undefined;
    const now = Date.now();
    const ms: Record<string, number> = {
        "24h": 24 * 3600 * 1000,
        "7d": 7 * 24 * 3600 * 1000,
        "30d": 30 * 24 * 3600 * 1000,
    };
    const delta = ms[w];
    if (!delta) return undefined;
    return new Date(now - delta).toISOString();
}

function relTime(iso: string): string {
    const then = new Date(iso).getTime();
    const sec = (Date.now() - then) / 1000;
    if (sec < 60) return `${Math.floor(sec)}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
}

export function AuditPage(): React.ReactElement {
    const me = useCurrentUser();
    const isAdmin = me.data?.roles.includes("admin");

    const [q, setQ] = useState("");
    const [actorKind, setActorKind] = useState<string>("");
    const [actionPrefix, setActionPrefix] = useState("");
    const [resourceType, setResourceType] = useState<string>("");
    const [timeWindow, setTimeWindow] = useState<string>("");
    const [drawerId, setDrawerId] = useState<number | null>(null);

    const filters: AuditFilters = useMemo(() => {
        const f: AuditFilters = { limit: 100 };
        if (q) f.q = q;
        if (actorKind) f.actor_kind = actorKind;
        if (actionPrefix) f.action = actionPrefix;
        if (resourceType) f.resource_type = resourceType;
        const from = windowToFromTS(timeWindow);
        if (from) f.from_ts = from;
        return f;
    }, [q, actorKind, actionPrefix, resourceType, timeWindow]);

    const list = useAuditList(filters);
    const verify = useAuditVerify();
    const [verifyOpen, setVerifyOpen] = useState(false);

    // ⌘R / Ctrl-R refreshes the audit list instead of the browser.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "r") {
                e.preventDefault();
                void list.refetch();
            }
            if (e.key === "Escape") {
                setDrawerId(null);
                setVerifyOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [list]);

    if (me.data && !isAdmin) {
        return <Navigate to="/" />;
    }

    const rows = list.data?.rows ?? [];
    const lastEvent = rows[0];

    const onVerify = () => {
        setVerifyOpen(true);
        verify.mutate({});
    };

    const onExport = async () => {
        try {
            await exportAuditNDJSON(filters);
        } catch (err) {
            // Simple alert; the surface for a rich toast is out of scope for
            // this initial route.
            alert(err instanceof Error ? err.message : "export failed");
        }
    };

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon"><ScrollText size={14} aria-hidden /></span>
                            Compliance
                        </span>
                        <h1 className="page-header__title">Audit log</h1>
                        <p className="page-header__description">
                            Append-only, HMAC-signed record of every consequential state change across
                            IntelRoom V2. Tamper-evident: any row whose body was edited after the fact
                            breaks verify at that row. Rotate the HMAC key quarterly (see runbook).
                        </p>
                    </div>
                    <div className="page-header__actions cluster-sm">
                        <button className="btn btn--ghost" onClick={() => void list.refetch()} aria-label="Refresh">
                            <RefreshCw size={14} aria-hidden /> Refresh
                        </button>
                        <button className="btn btn--ghost" onClick={onVerify}>
                            <ShieldCheck size={14} aria-hidden /> Verify chain
                        </button>
                        <button className="btn btn--primary" onClick={() => void onExport()}>
                            <Download size={14} aria-hidden /> Export NDJSON
                        </button>
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className="page-header__meta__item tabular-nums">
                        {rows.length.toLocaleString()} rows shown
                    </span>
                    {lastEvent && (
                        <span className="page-header__meta__item">
                            Last event {relTime(lastEvent.ts)}
                        </span>
                    )}
                </div>
            </header>

            <section className="section-card">
                <div className="toolbar" role="search">
                    <input
                        className="input"
                        placeholder="Search action or payload…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        aria-label="Search audit events"
                    />
                    <select
                        className="select"
                        value={actorKind}
                        onChange={(e) => setActorKind(e.target.value)}
                        aria-label="Actor kind"
                    >
                        {ACTOR_KINDS.map((k) => (
                            <option key={k || "any"} value={k}>
                                {k || "Any actor"}
                            </option>
                        ))}
                    </select>
                    <select
                        className="select"
                        value={resourceType}
                        onChange={(e) => setResourceType(e.target.value)}
                        aria-label="Resource type"
                    >
                        {RESOURCE_TYPES.map((t) => (
                            <option key={t || "any"} value={t}>
                                {t || "Any resource"}
                            </option>
                        ))}
                    </select>
                    <input
                        className="input"
                        placeholder="action prefix (e.g. tenant.)"
                        value={actionPrefix}
                        onChange={(e) => setActionPrefix(e.target.value)}
                        style={{ maxWidth: 220 }}
                        aria-label="Action prefix"
                    />
                    <select
                        className="select"
                        value={timeWindow}
                        onChange={(e) => setTimeWindow(e.target.value)}
                        aria-label="Time window"
                    >
                        {TIME_WINDOWS.map((w) => (
                            <option key={w.value} value={w.value}>{w.label}</option>
                        ))}
                    </select>
                </div>

                {list.isLoading && <TableSkeleton rows={8} columns={6} />}
                {list.isError && (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load audit log</div>
                        <div className="empty-state__body">
                            {list.error instanceof Error ? list.error.message : "Unknown error"}
                        </div>
                    </div>
                )}
                {list.data && rows.length === 0 && (
                    <div className="empty-state">
                        <ScrollText size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No matching events</div>
                        <div className="empty-state__body">
                            Try clearing filters or widening the time window.
                        </div>
                    </div>
                )}
                {rows.length > 0 && (
                    <div className="table-wrap">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th scope="col">Time</th>
                                    <th scope="col">Actor</th>
                                    <th scope="col">Action</th>
                                    <th scope="col">Resource</th>
                                    <th scope="col">Tenant</th>
                                    <th scope="col">IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <AuditRow key={row.id} row={row} onOpen={setDrawerId} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {drawerId !== null && (
                <AuditDrawer id={drawerId} onClose={() => setDrawerId(null)} />
            )}

            {verifyOpen && (
                <VerifyDialog
                    onClose={() => setVerifyOpen(false)}
                    busy={verify.isPending}
                    result={verify.data}
                    err={verify.error instanceof Error ? verify.error.message : undefined}
                />
            )}
        </div>
    );
}

function AuditRow({ row, onOpen }: { row: AuditEvent; onOpen: (id: number) => void }): React.ReactElement {
    return (
        <tr onClick={() => onOpen(row.id)} style={{ cursor: "pointer" }} tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter") onOpen(row.id); }}>
            <td title={row.ts}>{relTime(row.ts)}</td>
            <td>{row.actor_email || row.actor_kind}</td>
            <td><code className="chip">{row.action}</code></td>
            <td>
                <span className="status-pill status-pill--neutral">{row.resource_type}</span>
                {row.resource_id && (
                    <span style={{ marginLeft: 8, color: "var(--color-text-subtle)", fontFamily: "var(--font-mono)", fontSize: "0.8em" }}>
                        {row.resource_id.length > 14 ? `${row.resource_id.slice(0, 14)}…` : row.resource_id}
                    </span>
                )}
            </td>
            <td>{row.tenant_slug || "—"}</td>
            <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.85em" }}>{row.ip || "—"}</td>
        </tr>
    );
}

function AuditDrawer({ id, onClose }: { id: number; onClose: () => void }): React.ReactElement {
    const { data, isLoading, isError, error } = useAuditEvent(id);
    return (
        <div className="dialog-backdrop" onClick={onClose}>
            <aside
                className="dialog-content dialog-content--wide"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label="Audit event detail"
            >
                <header className="dialog-header">
                    <h2>Audit event #{id}</h2>
                    <button className="btn btn--ghost" aria-label="Close" onClick={onClose}>
                        <X size={16} aria-hidden />
                    </button>
                </header>
                {isLoading && <div className="stack-sm">Loading…</div>}
                {isError && <div className="empty-state">Error: {error instanceof Error ? error.message : "unknown"}</div>}
                {data && (
                    <div className="stack-md">
                        <dl className="kv">
                            <dt>Timestamp</dt><dd>{data.ts}</dd>
                            <dt>Actor</dt><dd>{data.actor_email || data.actor_kind}</dd>
                            <dt>Action</dt><dd><code className="chip">{data.action}</code></dd>
                            <dt>Resource</dt><dd>{data.resource_type} / <span style={{ fontFamily: "var(--font-mono)" }}>{data.resource_id}</span></dd>
                            {data.tenant_slug && (<><dt>Tenant</dt><dd>{data.tenant_slug}</dd></>)}
                            {data.ip && (<><dt>IP</dt><dd style={{ fontFamily: "var(--font-mono)" }}>{data.ip}</dd></>)}
                            <dt>Verified</dt>
                            <dd>
                                {data.verified ? (
                                    <span className="status-pill status-pill--live"><ShieldCheck size={12} aria-hidden /> verified</span>
                                ) : (
                                    <span className="status-pill status-pill--failed"><ShieldX size={12} aria-hidden /> tampered</span>
                                )}
                            </dd>
                        </dl>
                        <section>
                            <h3 className="section-heading">Payload</h3>
                            <pre className="code-block" style={{ maxHeight: 360, overflow: "auto" }}>
                                {JSON.stringify(data.payload, null, 2)}
                            </pre>
                        </section>
                        <section>
                            <h3 className="section-heading">Chain hashes</h3>
                            <dl className="kv">
                                <dt>prev_hash</dt><dd style={{ fontFamily: "var(--font-mono)", wordBreak: "break-all" }}>{hashToHex(data.prev_hash)}</dd>
                                <dt>hash</dt><dd style={{ fontFamily: "var(--font-mono)", wordBreak: "break-all" }}>{hashToHex(data.hash)}</dd>
                            </dl>
                        </section>
                    </div>
                )}
            </aside>
        </div>
    );
}

function hashToHex(h: AuditEvent["hash"]): string {
    if (!h) return "—";
    if (typeof h === "string") return h;
    if (Array.isArray(h)) {
        return h.map((b) => b.toString(16).padStart(2, "0")).join("");
    }
    return "—";
}

function VerifyDialog({
    onClose,
    busy,
    result,
    err,
}: {
    onClose: () => void;
    busy: boolean;
    result: ReturnType<typeof useAuditVerify>["data"];
    err?: string;
}): React.ReactElement {
    return (
        <div className="dialog-backdrop" onClick={onClose}>
            <div
                className="dialog-content"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label="Verify chain"
            >
                <header className="dialog-header">
                    <h2>Chain verification</h2>
                    <button className="btn btn--ghost" aria-label="Close" onClick={onClose}>
                        <X size={16} aria-hidden />
                    </button>
                </header>
                {busy && <div className="stack-sm">Walking chain… (this may take a moment on large logs)</div>}
                {err && <div className="empty-state">Verify failed: {err}</div>}
                {result && result.ok && (
                    <div className="empty-state" style={{ color: "var(--color-success)" }}>
                        <ShieldCheck size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Chain is intact</div>
                        <div className="empty-state__body">
                            {result.scanned.toLocaleString()} rows verified end-to-end.
                        </div>
                    </div>
                )}
                {result && !result.ok && (
                    <div className="empty-state" style={{ color: "var(--color-danger)" }}>
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Chain broken at row #{result.first_mismatch_id}</div>
                        <div className="empty-state__body stack-xs">
                            <div>Scanned {result.scanned.toLocaleString()} rows before detecting mismatch.</div>
                            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.85em" }}>
                                expected: {result.first_mismatch_expected}
                            </div>
                            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.85em" }}>
                                actual:   {result.first_mismatch_actual}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
